import React, { useState, useEffect } from 'react'
import { 
  Package, Plus, Edit2, Trash2, Save, X, 
  Gift, Star, DollarSign, Eye, EyeOff,
  TrendingUp, Clock, Users
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface Reward {
  id: string
  name: string
  cost_points: number
  description?: string
  category?: string
  availability: 'always' | 'limited' | 'seasonal'
  max_per_week?: number
  stock_remaining?: number
  active: boolean
  requires_parent_approval: boolean
  icon?: string
  user_redemption_count?: number
}

const CATEGORIES = [
  { value: 'privileges', label: 'Privileges', icon: Star, color: 'bg-purple-500' },
  { value: 'screen_time', label: 'Screen Time', icon: Clock, color: 'bg-blue-500' },
  { value: 'treats', label: 'Treats', icon: Gift, color: 'bg-pink-500' },
  { value: 'social', label: 'Social', icon: Users, color: 'bg-green-500' },
  { value: 'money', label: 'Money', icon: DollarSign, color: 'bg-yellow-500' }
]

export const RewardsManager: React.FC = () => {
  const [rewards, setRewards] = useState<Reward[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddReward, setShowAddReward] = useState(false)
  const [editingReward, setEditingReward] = useState<Reward | null>(null)
  const [filter, setFilter] = useState<'all' | 'active' | 'disabled'>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showDisabled, setShowDisabled] = useState(false)
  
  const [newReward, setNewReward] = useState<Partial<Reward>>({
    name: '',
    cost_points: 50,
    description: '',
    category: 'privileges',
    availability: 'always',
    active: true,
    requires_parent_approval: true
  })

  useEffect(() => {
    loadRewards()
  }, [])

  const loadRewards = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/rewards')
      setRewards(data)
    } catch (error) {
      console.error('Failed to load rewards:', error)
      toast.error('Failed to load rewards')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateReward = async () => {
    if (!newReward.name || !newReward.cost_points) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await apiClient.post('/api/rewards', newReward)
      toast.success('Reward created successfully')
      setShowAddReward(false)
      setNewReward({
        name: '',
        cost_points: 50,
        description: '',
        category: 'privileges',
        availability: 'always',
        active: true,
        requires_parent_approval: true
      })
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create reward')
    }
  }

  const handleUpdateReward = async () => {
    if (!editingReward) return

    try {
      await apiClient.patch(`/api/rewards/${editingReward.id}`, editingReward)
      toast.success('Reward updated successfully')
      setEditingReward(null)
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update reward')
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    if (!confirm('Are you sure you want to delete this reward?')) return
    
    try {
      await apiClient.delete(`/api/rewards/${rewardId}`)
      toast.success('Reward deleted successfully')
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete reward')
    }
  }

  const handleToggleActive = async (rewardId: string, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/rewards/${rewardId}`, { active: !isActive })
      toast.success(`Reward ${!isActive ? 'enabled' : 'disabled'}`)
      loadRewards()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update reward')
    }
  }

  const filteredRewards = rewards.filter(reward => {
    const matchesSearch = reward.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          reward.description?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesFilter = filter === 'all' ||
                          (filter === 'active' && reward.active) ||
                          (filter === 'disabled' && !reward.active)

    const matchesCategory = categoryFilter === 'all' || reward.category === categoryFilter

    if (!showDisabled && !reward.active) return false

    return matchesSearch && matchesFilter && matchesCategory
  })

  const getCategoryInfo = (category: string) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0]
  }

  const activeRewardsCount = rewards.filter(r => r.active).length
  const disabledRewardsCount = rewards.filter(r => !r.active).length

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Rewards Management</h2>
        <p className="text-gray-600">Create and manage rewards that kids can redeem</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Rewards</p>
              <p className="text-2xl font-bold text-green-600">{activeRewardsCount}</p>
            </div>
            <Gift className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Disabled Rewards</p>
              <p className="text-2xl font-bold text-gray-600">{disabledRewardsCount}</p>
            </div>
            <EyeOff className="w-8 h-8 text-gray-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Redemptions</p>
              <p className="text-2xl font-bold text-blue-600">
                {rewards.reduce((sum, r) => sum + (r.user_redemption_count || 0), 0)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Cost</p>
              <p className="text-2xl font-bold text-purple-600">
                {Math.round(rewards.reduce((sum, r) => sum + r.cost_points, 0) / (rewards.length || 1))}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search rewards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowDisabled(!showDisabled)}
              className={`px-4 py-2 rounded-lg flex items-center ${
                showDisabled ? 'bg-gray-200' : 'bg-gray-100'
              }`}
            >
              {showDisabled ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
              {showDisabled ? 'Hide' : 'Show'} Disabled
            </button>
          </div>
          <button
            onClick={() => setShowAddReward(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Reward
          </button>
        </div>
      </div>

      {/* Rewards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredRewards.map(reward => {
          const categoryInfo = getCategoryInfo(reward.category || 'privileges')

          return (
            <div
              key={reward.id}
              className={`bg-white rounded-lg shadow p-4 ${!reward.active ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${categoryInfo.color} bg-opacity-10 mr-3`}>
                    <categoryInfo.icon className={`w-5 h-5 ${categoryInfo.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{reward.name}</h3>
                    <span className="text-xs text-gray-500">{categoryInfo.label}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setEditingReward(reward)}
                    className="p-1 text-blue-600 hover:text-blue-900"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(reward.id, reward.active)}
                    className="p-1 text-yellow-600 hover:text-yellow-900"
                  >
                    {reward.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDeleteReward(reward.id)}
                    className="p-1 text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-blue-600">
                    {reward.cost_points} pts
                  </span>
                  {reward.requires_parent_approval && (
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                      Requires Approval
                    </span>
                  )}
                </div>
                {reward.description && (
                  <p className="text-sm text-gray-600">{reward.description}</p>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                {reward.max_per_week && (
                  <span>
                    Max {reward.max_per_week}/week
                  </span>
                )}
                <span className={`px-2 py-1 rounded ${
                  reward.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {reward.active ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Disabled Rewards Section */}
      {showDisabled && disabledRewardsCount > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Disabled Rewards ({disabledRewardsCount})
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {rewards.filter(r => !r.active).map(reward => (
                <div key={reward.id} className="bg-white rounded p-3 shadow-sm">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium">{reward.name}</h4>
                      <p className="text-sm text-gray-500">{reward.cost_points} points</p>
                    </div>
                    <button
                      onClick={() => handleToggleActive(reward.id, false)}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                    >
                      Enable
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Reward Modal */}
      {(showAddReward || editingReward) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingReward ? 'Edit Reward' : 'Add New Reward'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={editingReward ? editingReward.name : newReward.name}
                  onChange={(e) => editingReward 
                    ? setEditingReward({ ...editingReward, name: e.target.value })
                    : setNewReward({ ...newReward, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Extra Screen Time"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Points *</label>
                  <input
                    type="number"
                    min="1"
                    value={editingReward ? editingReward.cost_points : newReward.cost_points}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, cost_points: parseInt(e.target.value) })
                      : setNewReward({ ...newReward, cost_points: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select
                    value={editingReward ? editingReward.category : newReward.category}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, category: e.target.value })
                      : setNewReward({ ...newReward, category: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editingReward ? editingReward.description || '' : newReward.description}
                  onChange={(e) => editingReward
                    ? setEditingReward({ ...editingReward, description: e.target.value })
                    : setNewReward({ ...newReward, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Describe the reward..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Availability</label>
                <select
                  value={editingReward ? editingReward.availability : newReward.availability}
                  onChange={(e) => editingReward
                    ? setEditingReward({ ...editingReward, availability: e.target.value as any })
                    : setNewReward({ ...newReward, availability: e.target.value as any })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="always">Always Available</option>
                  <option value="limited">Limited Quantity</option>
                  <option value="seasonal">Seasonal</option>
                </select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingReward ? editingReward.requires_parent_approval : newReward.requires_parent_approval}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, requires_parent_approval: e.target.checked })
                      : setNewReward({ ...newReward, requires_parent_approval: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm">Requires parent approval</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingReward ? editingReward.active : newReward.active}
                    onChange={(e) => editingReward
                      ? setEditingReward({ ...editingReward, active: e.target.checked })
                      : setNewReward({ ...newReward, active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm">Active</label>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddReward(false)
                  setEditingReward(null)
                  setNewReward({
                    name: '',
                    cost_points: 50,
                    description: '',
                    category: 'privileges',
                    availability: 'always',
                    active: true,
                    requires_parent_approval: true
                  })
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={editingReward ? handleUpdateReward : handleCreateReward}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingReward ? 'Save Changes' : 'Create Reward'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}